import { z } from 'zod';

const IdSchema = z
  .string()
  .min(3)
  .max(160)
  .regex(/^[a-z][a-z0-9]*(?:[._:-][a-z0-9]+)*$/);
const CopyKeySchema = IdSchema;
const FieldPathSchema = z
  .string()
  .min(1)
  .max(240)
  .regex(/^[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*$/);

const JsonPrimitiveSchema = z.union([
  z.string().max(4_096),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([JsonPrimitiveSchema, z.array(JsonValueSchema), z.record(JsonValueSchema)]),
);

export type BindingExpr =
  | { readonly kind: 'field'; readonly path: string }
  | { readonly kind: 'literal'; readonly value: JsonValue }
  | { readonly kind: 'coalesce'; readonly values: readonly BindingExpr[] }
  | {
      readonly kind: 'eq' | 'in' | 'and' | 'or';
      readonly operands: readonly BindingExpr[];
    }
  | { readonly kind: 'not' | 'exists'; readonly operand: BindingExpr }
  | {
      readonly kind: 'format' | 'aggregate';
      readonly id: string;
      readonly input: BindingExpr;
    };

export const BindingExprSchema: z.ZodType<BindingExpr> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('field'), path: FieldPathSchema }).strict(),
    z.object({ kind: z.literal('literal'), value: JsonValueSchema }).strict(),
    z
      .object({
        kind: z.literal('coalesce'),
        values: z.array(BindingExprSchema).min(2).max(10),
      })
      .strict(),
    z
      .object({
        kind: z.enum(['eq', 'in', 'and', 'or']),
        operands: z.array(BindingExprSchema).min(2).max(20),
      })
      .strict(),
    z
      .object({
        kind: z.enum(['not', 'exists']),
        operand: BindingExprSchema,
      })
      .strict(),
    z
      .object({
        kind: z.enum(['format', 'aggregate']),
        id: IdSchema,
        input: BindingExprSchema,
      })
      .strict(),
  ]),
);

export const InternalNavigationTargetSchema = z
  .object({
    kind: z.literal('screen'),
    surface: z.enum(['public', 'ops', 'marketing']),
    screenId: IdSchema,
  })
  .strict();
export type InternalNavigationTarget = z.infer<typeof InternalNavigationTargetSchema>;

export const ActionSpecSchema = z
  .object({
    actionId: IdSchema,
    operationId: IdSchema,
    input: BindingExprSchema,
    confirmationCopyKey: CopyKeySchema.optional(),
    successCopyKey: CopyKeySchema.optional(),
    onSuccess: InternalNavigationTargetSchema.optional(),
  })
  .strict();
export type ActionSpec = z.infer<typeof ActionSpecSchema>;

const UiNodeTypeSchema = z.enum([
  'Page',
  'Stack',
  'Grid',
  'Section',
  'Tabs',
  'Accordion',
  'Drawer',
  'Wizard',
  'Text',
  'Image',
  'Metric',
  'Badge',
  'Alert',
  'Help',
  'TrustBadge',
  'List',
  'Table',
  'Detail',
  'Chart',
  'Timeline',
  'Form',
  'Field',
  'Select',
  'DateTime',
  'Submit',
  'Confirmation',
  'Link',
  'Breadcrumb',
  'CTA',
  'DomainComponent',
]);

const FORBIDDEN_PROP_KEYS = new Set([
  'dangerouslySetInnerHTML',
  'innerHTML',
  'outerHTML',
  'html',
  'rawHtml',
  'script',
  'style',
  'css',
  'url',
  'href',
  'src',
]);

const UiNodeSchema = z
  .object({
    nodeId: IdSchema,
    type: UiNodeTypeSchema,
    children: z.array(IdSchema).max(100),
    props: z.record(JsonValueSchema),
    action: ActionSpecSchema.optional(),
    actions: z.array(ActionSpecSchema).max(12).optional(),
    visibleWhen: BindingExprSchema.optional(),
    componentId: IdSchema.optional(),
  })
  .strict()
  .superRefine((node, ctx) => {
    for (const key of Object.keys(node.props)) {
      if (FORBIDDEN_PROP_KEYS.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['props', key],
          message: `unsafe UI prop ${key} is forbidden`,
        });
      }
    }
    if (node.type === 'DomainComponent' && !node.componentId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['componentId'],
        message: 'DomainComponent requires a whitelisted componentId',
      });
    }
    if (node.actions && node.type !== 'DomainComponent') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions'],
        message: 'multiple actions are allowed only on a whitelisted DomainComponent',
      });
    }
    if (node.action && node.actions) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actions'],
        message: 'use either action or actions, not both',
      });
    }
    const actionIds = new Set<string>();
    for (const action of node.actions ?? []) {
      if (actionIds.has(action.actionId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actions'],
          message: `duplicate action id ${action.actionId}`,
        });
      }
      actionIds.add(action.actionId);
    }
    if (node.type === 'Tabs' && node.children.length > 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['children'],
        message: 'Tabs may contain at most five items',
      });
    }
    if ((node.type === 'CTA' || node.type === 'Submit') && node.props['minTouchSize'] !== 44) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['props', 'minTouchSize'],
        message: 'interactive touch targets must declare minTouchSize=44',
      });
    }
  });
export type UiNode = z.infer<typeof UiNodeSchema>;

export const UiNavigationContributionV1Schema = z
  .object({
    labelCopyKey: CopyKeySchema,
    groupId: IdSchema,
    groupCopyKey: CopyKeySchema,
    order: z.number().int().min(0).max(1_000),
    groupOrder: z.number().int().min(0).max(1_000),
    iconId: IdSchema,
    defaultOpen: z.boolean(),
    standalone: z.boolean(),
  })
  .strict();
export type UiNavigationContributionV1 = z.infer<typeof UiNavigationContributionV1Schema>;

export const UiDocumentV1Schema = z
  .object({
    schemaVersion: z.literal('1'),
    screenId: IdSchema,
    surface: z.enum(['public', 'ops', 'marketing']),
    routeId: IdSchema,
    titleCopyKey: CopyKeySchema,
    navigation: UiNavigationContributionV1Schema.optional(),
    requiredCapabilities: z.array(IdSchema),
    requiredPermissions: z.array(IdSchema),
    dataSources: z
      .array(
        z
          .object({
            sourceId: IdSchema,
            operationId: IdSchema,
            bindAs: FieldPathSchema,
            input: BindingExprSchema.optional(),
          })
          .strict(),
      )
      .max(20),
    rootNodeId: IdSchema,
    nodes: z.record(UiNodeSchema),
    states: z
      .object({
        loadingCopyKey: CopyKeySchema,
        emptyCopyKey: CopyKeySchema,
        errorCopyKey: CopyKeySchema,
        successCopyKey: CopyKeySchema,
      })
      .strict(),
    responsive: z
      .object({
        mobileColumns: z.number().int().min(1).max(4),
        desktopColumns: z.number().int().min(1).max(24),
        progressiveDisclosure: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type UiDocumentV1 = z.infer<typeof UiDocumentV1Schema>;

function validateGraph(document: UiDocumentV1): void {
  const entries = Object.entries(document.nodes);
  if (entries.length > 500) {
    throw new Error('UI document exceeds the 500 node limit');
  }
  for (const [key, node] of entries) {
    if (key !== node.nodeId) {
      throw new Error(`UI node key ${key} does not match nodeId ${node.nodeId}`);
    }
  }
  if (!document.nodes[document.rootNodeId]) {
    throw new Error(`UI root node ${document.rootNodeId} is missing`);
  }
  let primaryCtaCount = 0;
  for (const node of Object.values(document.nodes)) {
    if (node.type === 'CTA' && node.props['emphasis'] === 'primary') {
      primaryCtaCount += 1;
    }
    for (const childId of node.children) {
      if (!document.nodes[childId]) {
        throw new Error(`UI child node ${childId} is missing`);
      }
    }
  }
  if (primaryCtaCount > 1) {
    throw new Error('A screen may contain at most one primary CTA');
  }

  const visiting = new Set<string>();
  const visitedDepth = new Map<string, number>();
  const visit = (nodeId: string, depth: number): void => {
    if (depth > 12) {
      throw new Error('UI tree depth exceeds the limit of twelve');
    }
    if (visiting.has(nodeId)) {
      throw new Error(`UI tree contains a cycle at ${nodeId}`);
    }
    if ((visitedDepth.get(nodeId) ?? 0) >= depth) {
      return;
    }
    visitedDepth.set(nodeId, depth);
    visiting.add(nodeId);
    const node = document.nodes[nodeId];
    if (!node) {
      throw new Error(`UI node ${nodeId} is missing`);
    }
    for (const child of node.children) {
      visit(child, depth + 1);
    }
    visiting.delete(nodeId);
  };
  visit(document.rootNodeId, 1);
}

export function parseUiDocument(raw: unknown): UiDocumentV1 {
  const document = UiDocumentV1Schema.parse(raw);
  const bytes = new TextEncoder().encode(JSON.stringify(document)).byteLength;
  if (bytes > 512 * 1_024) {
    throw new Error('UI surface manifest exceeds 512 KB');
  }
  validateGraph(document);
  return document;
}

export interface TableWorkspaceColumn {
  readonly field: string;
  readonly labelCopyKey: string;
  readonly valueCopyKeys?: Readonly<Record<string, string>>;
}

export interface TableWorkspaceDocumentInput {
  readonly screenId: string;
  readonly routeId: string;
  readonly titleCopyKey: string;
  readonly eyebrowCopyKey: string;
  readonly descriptionCopyKey: string;
  readonly sectionTitleCopyKey: string;
  readonly sectionDescriptionCopyKey: string;
  readonly emptyCopyKey: string;
  readonly guidanceTitleCopyKey: string;
  readonly guidanceCopyKey: string;
  readonly requiredCapabilities: readonly string[];
  readonly requiredPermissions: readonly string[];
  readonly queryOperationId: string;
  readonly queryInput: JsonValue;
  readonly bindAs: string;
  readonly rowsPath: string;
  readonly columns: readonly TableWorkspaceColumn[];
}

/**
 * Safe authoring helper for scan-first read models. The returned value is the
 * same fully serializable UiDocumentV1 as hand-authored metadata; no function,
 * JSX, HTML or executable expression is stored in the document.
 */
export function createTableWorkspaceDocument(input: TableWorkspaceDocumentInput): UiDocumentV1 {
  const prefix = input.screenId;
  const pageId = `${prefix}.page`;
  const sectionId = `${prefix}.section`;
  const tableId = `${prefix}.table`;
  const guidanceId = `${prefix}.guidance`;
  const guidanceCopyId = `${prefix}.guidance-copy`;
  return parseUiDocument({
    schemaVersion: '1',
    screenId: input.screenId,
    surface: 'ops',
    routeId: input.routeId,
    titleCopyKey: input.titleCopyKey,
    requiredCapabilities: input.requiredCapabilities,
    requiredPermissions: input.requiredPermissions,
    dataSources: [
      {
        sourceId: `${prefix}.query`,
        operationId: input.queryOperationId,
        bindAs: input.bindAs,
        input: { kind: 'literal', value: input.queryInput },
      },
    ],
    rootNodeId: pageId,
    nodes: {
      [pageId]: {
        nodeId: pageId,
        type: 'Page',
        children: [sectionId, guidanceId],
        props: {
          eyebrowCopyKey: input.eyebrowCopyKey,
          titleCopyKey: input.titleCopyKey,
          descriptionCopyKey: input.descriptionCopyKey,
        },
      },
      [sectionId]: {
        nodeId: sectionId,
        type: 'Section',
        children: [tableId],
        props: {
          titleCopyKey: input.sectionTitleCopyKey,
          descriptionCopyKey: input.sectionDescriptionCopyKey,
        },
      },
      [tableId]: {
        nodeId: tableId,
        type: 'Table',
        children: [],
        props: {
          rows: { kind: 'field', path: input.rowsPath },
          columns: input.columns,
          emptyCopyKey: input.emptyCopyKey,
        },
      },
      [guidanceId]: {
        nodeId: guidanceId,
        type: 'Accordion',
        children: [guidanceCopyId],
        props: {
          labelCopyKey: input.guidanceTitleCopyKey,
        },
      },
      [guidanceCopyId]: {
        nodeId: guidanceCopyId,
        type: 'Text',
        children: [],
        props: {
          copyKey: input.guidanceCopyKey,
        },
      },
    },
    states: {
      loadingCopyKey: 'state.loading',
      emptyCopyKey: 'state.empty',
      errorCopyKey: 'state.error',
      successCopyKey: 'state.success',
    },
    responsive: {
      mobileColumns: 1,
      desktopColumns: 12,
      progressiveDisclosure: true,
    },
  });
}
